import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { MAT_LEGACY_CHECKBOX_DEFAULT_OPTIONS as MAT_CHECKBOX_DEFAULT_OPTIONS } from '@angular/material/legacy-checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyListModule as MatListModule } from '@angular/material/legacy-list';
import { MAT_LEGACY_RADIO_DEFAULT_OPTIONS as MAT_RADIO_DEFAULT_OPTIONS } from '@angular/material/legacy-radio';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { CorrelationIdInterceptor } from '@aya/common/interceptors';
import { Loader } from '@googlemaps/js-api-loader';
import { EffectsModule } from '@ngrx/effects';
import { provideRouterStore, StoreRouterConnectingModule } from '@ngrx/router-store';
import { provideStore, StoreModule } from '@ngrx/store';
import { provideStoreDevtools, StoreDevtoolsModule } from '@ngrx/store-devtools';
import { ICON_SETTINGS, IconSettingsService, IconsModule } from '@progress/kendo-angular-icons';
import { NgChartsModule } from 'ng2-charts';
import { CoreEffects } from 'src/app/core/effects/core.effects';
import { reducers } from 'src/app/core/router.reducer';
import { TopNavMenuComponent } from 'src/app/nav-menu/top-nav-menu/top-nav-menu.component';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { environment } from 'src/environments/environment';
import { ENV, getEnv } from 'src/environments/environment.provider';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CatchAllComponent } from './catchall.component';
import { NSwagMigrationInterceptor } from './compliance/api/migration/nswag-migration.interceptor';
import { API_BASE_URL } from './compliance/api/misc/api-base-url.token';
import { AppSettings } from './config/app-settings';
import { APP_CONFIG, Settings } from './config/settings';
import { AppIdInterceptor } from './core/app-id-interceptor';
import { AuthInterceptor } from './core/auth.interceptor';
import { DatadogLoggerComponent } from './datadog-logger/datadog-logger.component';
import { ApiCacheInterceptor } from './facility-contracts/shared/interceptors/api-cache.interceptor';
import { MenuItemDirective } from './nav-menu/menu-item.directive';
import { NavMenuLinkComponent } from './nav-menu/nav-menu-link.component';
import { NavMenuComponent } from './nav-menu/nav-menu.component';
import { RootStoreModule } from './root-store/root-store.module';
import { NovaErrorHandler } from './shared/services/nova-error-handler';
import { SharedModule } from './shared/shared.module';
import { WelcomeComponent } from './welcome.component';
import { NewVersionMessageComponent } from 'src/app/shared/components/new-version-message/new-version-message.component';
import { LoggerService } from 'src/app/shared/services/logger.service';
import { NovaKendoIconsService } from 'src/app/core/nova-icons.service';
import { AuthModule as OAuthModule, AuthHttpInterceptor } from '@auth0/auth0-angular';
import { IdentityService } from './core/identity.service';
import { firstValueFrom } from 'rxjs';
import { FeatureFlag } from './shared/models/enums/feature-flag';
import { MAT_TABS_CONFIG } from '@angular/material/tabs';

const initApplication =
    (ldFeatureManager: LDFeatureManager, settings: Settings, logger: LoggerService, identityService: IdentityService) =>
    async () => {
        await logger.init();

        // Load Google API via the JS library loader
        // See here: https://developers.google.com/maps/documentation/javascript/overview#js_api_loader_package
        const loader = new Loader({
            apiKey: settings.googleMapsKey,
            libraries: ['places'],
            version: 'weekly'
        });

        await loader.importLibrary('maps');
        await ldFeatureManager.init();
        const auth0EnforceNovaOAuthFlag = await firstValueFrom(
            ldFeatureManager.isEnabled(FeatureFlag.Auth0EnforceNovaOAuth)
        );
        identityService.init(auth0EnforceNovaOAuthFlag);
    };
@NgModule({
    imports: [
        // angular deps
        BrowserModule,
        BrowserAnimationsModule,
        HttpClientModule,
        // nova modules
        AppRoutingModule,
        SharedModule,
        StoreModule.forRoot(reducers, {
            runtimeChecks: {
                strictStateImmutability: true,
                strictActionImmutability: true,
                strictStateSerializability: true,
                strictActionSerializability: false,
                strictActionWithinNgZone: true,
                strictActionTypeUniqueness: true
            }
        }),
        StoreDevtoolsModule.instrument({
            name: 'Nova DevTools',
            logOnly: environment.production,
            connectInZone: true
        }),
        EffectsModule.forRoot([CoreEffects]),
        StoreRouterConnectingModule.forRoot(),
        RootStoreModule,
        IconsModule,
        RouterModule,
        MatIconModule,
        MatListModule,

        NgChartsModule,
        WelcomeComponent,
        CatchAllComponent,
        DatadogLoggerComponent,
        NavMenuComponent,
        NavMenuLinkComponent,
        MenuItemDirective,
        NewVersionMessageComponent,
        OAuthModule.forRoot(),
        TopNavMenuComponent,
        MenuItemDirective
    ],
    bootstrap: [AppComponent],
    declarations: [AppComponent],
    providers: [
        provideStore(reducers, {
            runtimeChecks: {
                strictStateImmutability: true,
                strictActionImmutability: true,
                strictStateSerializability: true,
                strictActionSerializability: false,
                strictActionWithinNgZone: true,
                strictActionTypeUniqueness: true
            }
        }),
        provideRouterStore(),
        provideStoreDevtools(),
        { provide: HTTP_INTERCEPTORS, useClass: CorrelationIdInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: AppIdInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: ApiCacheInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: NSwagMigrationInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true },
        { provide: APP_CONFIG, useValue: AppSettings },
        { provide: ENV, useFactory: getEnv },
        {
            provide: APP_INITIALIZER,
            useFactory: initApplication,
            multi: true,
            deps: [LDFeatureManager, APP_CONFIG, LoggerService, IdentityService]
        },
        { provide: ErrorHandler, useClass: NovaErrorHandler },
        {
            provide: API_BASE_URL,
            useFactory: (settings: Settings) => settings.coreAPIUrl?.replace(/\/$/, ''),
            multi: true,
            deps: [APP_CONFIG]
        },
        {
            provide: MAT_CHECKBOX_DEFAULT_OPTIONS,
            useValue: {
                color: 'primary'
            }
        },
        {
            provide: MAT_RADIO_DEFAULT_OPTIONS,
            useValue: {
                color: 'primary'
            }
        },
        { provide: LOCALE_ID, useValue: 'en-US' },
        {
            provide: ICON_SETTINGS,
            useValue: {
                type: 'svg'
            }
        },
        {
            provide: IconSettingsService,
            useClass: NovaKendoIconsService
        },
        { provide: MAT_TABS_CONFIG, useValue: { stretchTabs: false } }
    ]
})
export class AppModule {}
